import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { NovelSummary } from "../apiClient.js";
import { fallbackCoverUrl } from "../lib/novel.js";
import { POSTER_ASPECT, POSTER_CSS_WIDTH, fallBackToSource, sizedImageUrl } from "../lib/imageUrl.js";
import { useT, type TranslationKey } from "../lib/i18n/store.js";

/**
 * Shown right after the author starts a generation. It sets the expectation for
 * how long it takes and offers a novel to play in the meantime — generation
 * keeps running server-side, and the topbar pill tracks it. Dismissing reveals
 * the live status page underneath.
 *
 * Portaled to document.body like the feedback modal: the sticky `.topbar`'s
 * backdrop-filter would otherwise trap a fixed-position overlay inside itself.
 */
export function GenerationStartedDialog({
  estimate,
  suggested,
  onPlay,
  onClose
}: {
  /** Translation key for the time estimate, e.g. "estimate.mediumImages". */
  estimate: TranslationKey;
  /** A novel to offer for the wait, or null when the catalog is empty. */
  suggested: NovelSummary | null;
  onPlay: (novelId: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Latest onClose behind a ref so the focus/keyboard effect can run exactly
  // once (mount → unmount). Depending on onClose directly would re-run the
  // effect on every parent re-render — and the status page polls the running job
  // once a second — repeatedly stealing focus from whatever the user selected.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Only dismiss on a backdrop click whose press ALSO started on the backdrop —
  // a text selection released over the backdrop otherwise closes the modal.
  const pointerDownOnBackdrop = useRef(false);

  // Move focus into the dialog on open, close on Escape, and restore focus on
  // close. (The invoking Create page has already unmounted by the time this
  // runs, so the restore target is usually the document body — harmless.)
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      className="admin-modal-backdrop"
      onMouseDown={(event) => {
        pointerDownOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pointerDownOnBackdrop.current) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="admin-modal gen-started-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gen-started-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="admin-modal-close" aria-label={t("popup.close")} onClick={onClose}>
          ×
        </button>
        <p className="eyebrow">{t("popup.eyebrow")}</p>
        <h2 id="gen-started-title">{t("popup.title")}</h2>
        <p className="gen-started-lead">{t("popup.lead", { estimate: t(estimate) })}</p>

        {suggested && (
          <div className="gen-started-suggestion">
            <p className="gen-started-suggestion-label">{t("popup.whileYouWait")}</p>
            <button
              type="button"
              className="gen-started-novel"
              onClick={() => onPlay(suggested.id)}
              aria-label={t("common.playTitle", { title: suggested.title })}
            >
              <span className="gen-started-cover">
                <img
                  src={sizedImageUrl(suggested.cover_asset_url, POSTER_CSS_WIDTH, { aspect: POSTER_ASPECT }) ?? fallbackCoverUrl}
                  onError={fallBackToSource(suggested.cover_asset_url)}
                  alt=""
                />
              </span>
              <span className="gen-started-novel-body">
                <span className="gen-started-novel-title">{suggested.title}</span>
                <span className="gen-started-novel-desc">{suggested.description}</span>
                {suggested.likes > 0 && (
                  <span className="gen-started-novel-likes" title={t("popup.likesTitle", { count: suggested.likes })}>
                    ♥ {suggested.likes}
                  </span>
                )}
              </span>
            </button>
          </div>
        )}

        <div className="button-row gen-started-actions">
          {suggested ? (
            <>
              <button type="button" onClick={() => onPlay(suggested.id)}>
                {t("popup.playNow")}
              </button>
              <button type="button" className="secondary" onClick={onClose}>
                {t("popup.watchProgress")}
              </button>
            </>
          ) : (
            <button type="button" onClick={onClose}>
              {t("popup.watchProgress")}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
